import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { MdOutlineArrowBack, MdRefresh, MdSend } from "react-icons/md";
import { LIVE_CHAT_COPY, LIVE_CHAT_LIMITS } from "../constants";
import { useLiveChat } from "../hooks/useLiveChat";
import "./liveChat.css";
const IMAGE_HOSTS = new Set([
  "logisy.s3.amazonaws.com",
  "logisy.s3.ap-south-1.amazonaws.com",
  "logisy.s3.us-east-1.amazonaws.com",
]);
const MESSAGE_PREVIEW_LENGTH = 500;
const DEFAULT_NOTIFICATION_SOUND_PATH = "/sounds/notification_sound.mp3";

export function getNotificationSoundUrl(
  configuredPath = process.env.REACT_APP_NOTIFICATION_SOUND_PATH
) {
  const soundPath = configuredPath?.trim() || DEFAULT_NOTIFICATION_SOUND_PATH;
  try {
    if (
      !soundPath.startsWith("/") ||
      soundPath.startsWith("//") ||
      soundPath.includes("\\")
    ) {
      throw new Error("Notification sound path must be root-relative.");
    }
    const soundUrl = new URL(soundPath, window.location.origin);
    if (
      soundUrl.origin !== window.location.origin ||
      !["http:", "https:"].includes(soundUrl.protocol)
    ) {
      throw new Error("Notification sound must use the Site Widget origin.");
    }
    return soundUrl.toString();
  } catch {
    return new URL(DEFAULT_NOTIFICATION_SOUND_PATH, window.location.origin).toString();
  }
}

function playNotificationSound() {
  try {
    const audio = new Audio(getNotificationSoundUrl());
    const playback = audio.play();
    playback?.catch?.(() => {});
  } catch {
    // Sound playback can be blocked by browser autoplay policies.
  }
}

function getSafeImageUrl(attachment) {
  try {
    const url = new URL(attachment?.url);
    return attachment?.content_type === "image" && url.protocol === "https:" && IMAGE_HOSTS.has(url.hostname)
      ? url.toString()
      : null;
  } catch {
    return null;
  }
}


function Message({ message, onRetry, onCancel }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const isCustomer = message.actor === "customer";
  const imageUrl = getSafeImageUrl(message.attachment);
  const messageText = typeof message.message === "string" ? message.message : "";
  const isLongMessage = messageText.length > MESSAGE_PREVIEW_LENGTH;
  const visibleMessage =
    isLongMessage && !isExpanded
      ? `${messageText.slice(0, MESSAGE_PREVIEW_LENGTH)}…`
      : messageText;

  return (
    <li className={`live-chat-message ${isCustomer ? "is-customer" : "is-brand"}`}>
      <div className="live-chat-bubble">
        {messageText && (
          <>
            <p>{visibleMessage}</p>
            {isLongMessage && (
              <button
                type="button"
                className="live-chat-read-more"
                aria-expanded={isExpanded}
                onClick={() => setIsExpanded((expanded) => !expanded)}
              >
                {isExpanded ? "Read less" : "Read more"}
              </button>
            )}
          </>
        )}
        {imageUrl && (
          <img src={imageUrl} alt={message.attachment.file_name || "Chat attachment"} />
        )}
        {!message.message && !imageUrl && <p>Message</p>}
      </div>
      {message.pending && (
        <span className="live-chat-message-status">
          {message.waitingForVerification ? "Waiting for verification" : "Sending…"}
        </span>
      )}
      {message.failed && (
        <div className="live-chat-message-failure">
          <span>{message.error || LIVE_CHAT_COPY.unavailable}</span>
          <button
            type="button"
            disabled={Boolean(message.retryAt && message.retryAt > Date.now())}
            onClick={() => onRetry(message.client_message_id)}
          >
            {LIVE_CHAT_COPY.retry}
          </button>
          <button type="button" onClick={() => onCancel(message.client_message_id)}>
            {LIVE_CHAT_COPY.cancel}
          </button>
        </div>
      )}
    </li>
  );
}

function SegmentedDigitInput({
  id,
  label,
  length,
  value,
  onChange,
  autoComplete,
  disabled = false,
}) {
  const inputRefs = useRef([]);
  const normalizedValue = typeof value === "string" ? value : "";
  const digits = Array.from({ length }, (_, index) => normalizedValue[index] || "");

  const updateDigits = (startIndex, rawValue) => {
    const incomingDigits = rawValue.replace(/\D/g, "");
    const nextDigits = [...digits];

    if (!incomingDigits) {
      nextDigits[startIndex] = "";
      onChange(nextDigits.join(""));
      return;
    }

    incomingDigits
      .slice(0, length - startIndex)
      .split("")
      .forEach((digit, offset) => {
        nextDigits[startIndex + offset] = digit;
      });
    onChange(nextDigits.join("").slice(0, length));

    const nextIndex = Math.min(startIndex + incomingDigits.length, length - 1);
    inputRefs.current[nextIndex]?.focus();
  };

  return (
    <div className={`live-chat-segmented-input is-${length}-digit`}>
      <span id={`${id}-label`} className="live-chat-segmented-label">
        {label}
      </span>
      <div
        className="live-chat-segmented-inputs"
        role="group"
        aria-labelledby={`${id}-label`}
        style={{ "--live-chat-digit-count": length }}
        onPaste={(event) => {
          const pastedDigits = event.clipboardData.getData("text").replace(/\D/g, "");
          if (!pastedDigits) return;
          event.preventDefault();
          onChange(pastedDigits.slice(0, length));
          inputRefs.current[Math.min(pastedDigits.length, length) - 1]?.focus();
        }}
      >
        {digits.map((digit, index) => (
          <input
            key={index}
            ref={(element) => {
              inputRefs.current[index] = element;
            }}
            id={`${id}-${index + 1}`}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={1}
            autoComplete={index === 0 ? autoComplete : "off"}
            aria-label={`${label} digit ${index + 1}`}
            value={digit}
            disabled={disabled}
            onChange={(event) => updateDigits(index, event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Backspace" && !digit && index > 0) {
                inputRefs.current[index - 1]?.focus();
              }
              if (event.key === "ArrowLeft" && index > 0) {
                event.preventDefault();
                inputRefs.current[index - 1]?.focus();
              }
              if (event.key === "ArrowRight" && index < length - 1) {
                event.preventDefault();
                inputRefs.current[index + 1]?.focus();
              }
            }}
          />
        ))}
      </div>
    </div>
  );
}

function IdentityForm({ chat }) {
  const [secondsRemaining, setSecondsRemaining] = useState(0);

  useEffect(() => {
    if (!chat.resendAvailableAt) return undefined;
    const update = () => {
      setSecondsRemaining(Math.max(0, Math.ceil((chat.resendAvailableAt - Date.now()) / 1000)));
    };
    update();
    const timer = window.setInterval(update, 1000);
    return () => window.clearInterval(timer);
  }, [chat.resendAvailableAt]);

  if (chat.status === "unsupported") {
    return <p className="live-chat-notice" role="alert">{LIVE_CHAT_COPY.unsupportedVerification}</p>;
  }

  if (chat.identityStep === "phone") {
    return (
      <form
        className="live-chat-identity"
        onSubmit={(event) => {
          event.preventDefault();
          chat.sendOtp();
        }}
      >
        <SegmentedDigitInput
          id="live-chat-phone"
          label={LIVE_CHAT_COPY.phoneLabel}
          length={10}
          value={chat.phone}
          autoComplete="tel"
          onChange={(value) => chat.setIdentityInput("phone", value)}
          disabled={Boolean(chat.identityRequest)}
        />
        {chat.identityError && <p role="alert">{chat.identityError}</p>}
        <button type="submit" className="live-chat-primary-button" disabled={chat.identityRequest === "sending"}>
          {chat.identityRequest === "sending" ? "Sending…" : LIVE_CHAT_COPY.sendOtp}
        </button>
      </form>
    );
  }

  if (chat.identityStep === "otp") {
    return (
      <form
        className="live-chat-identity"
        onSubmit={(event) => {
          event.preventDefault();
          chat.verifyOtp();
        }}
      >
        <SegmentedDigitInput
          id="live-chat-otp"
          label={LIVE_CHAT_COPY.otpLabel}
          length={4}
          value={chat.otp}
          autoComplete="one-time-code"
          onChange={(value) => chat.setIdentityInput("otp", value)}
          disabled={Boolean(chat.identityRequest)}
        />
        {chat.identityError && <p role="alert">{chat.identityError}</p>}
        <button type="submit" className="live-chat-primary-button" disabled={Boolean(chat.identityRequest)}>
          {chat.identityRequest === "verifying" ? "Verifying…" : LIVE_CHAT_COPY.verifyOtp}
        </button>
        <div className="live-chat-identity-actions">
          <button type="button" onClick={chat.editPhone} disabled={Boolean(chat.identityRequest)}>{LIVE_CHAT_COPY.changePhone}</button>
          <button type="button" onClick={chat.sendOtp} disabled={secondsRemaining > 0 || Boolean(chat.identityRequest)}>
            {chat.identityRequest === "sending"
              ? "Sending…"
              : secondsRemaining > 0
                ? `${LIVE_CHAT_COPY.resendOtp} (${secondsRemaining}s)`
                : LIVE_CHAT_COPY.resendOtp}
          </button>
        </div>
      </form>
    );
  }

  return null;
}

function ChatLoadingSkeleton() {
  return (
    <div className="live-chat-loading-skeleton" role="status" aria-label="Loading chat">
      <span className="sr-only">Loading chat…</span>
      <div className="live-chat-skeleton-messages" aria-hidden="true">
        <div className="live-chat-skeleton-bubble is-brand is-medium" />
        <div className="live-chat-skeleton-bubble is-customer is-short" />
        <div className="live-chat-skeleton-bubble is-brand is-wide" />
        <div className="live-chat-skeleton-bubble is-customer is-medium" />
        <div className="live-chat-skeleton-bubble is-brand is-short" />
        <div className="live-chat-skeleton-bubble is-customer" />
        <div className="live-chat-skeleton-bubble is-brand is-medium" />
      </div>
      <div className="live-chat-skeleton-composer" aria-hidden="true">
        <div className="live-chat-skeleton-input" />
        <div className="live-chat-skeleton-send" />
      </div>
    </div>
  );
}

export default function LiveChat({
  settings,
  domain,
  merchantId,
  mode,
  baseUrl,
  staffToken,
  active = false,
  onUnreadChange,
  onBack,
}) {
  const chat = useLiveChat({
    liveChatConfig: settings.live_chat,
    merchantId,
    domain,
    mode,
    baseUrl,
    staffToken,
    active,
  });
  const [draft, setDraft] = useState("");
  const [unreadCount, setUnreadCount] = useState(0);
  const transcriptRef = useRef(null);
  const previousScrollHeightRef = useRef(0);
  const shouldStickToBottomRef = useRef(true);
  const seenMessagesRef = useRef(new Set());
  const unreadInitializedRef = useRef(false);
  const historyThrottleRef = useRef(0);
  const lastTranscriptScrollTopRef = useRef(0);

  useEffect(() => {
    onUnreadChange?.(unreadCount);
  }, [onUnreadChange, unreadCount]);

  useEffect(() => {
    if (active) setUnreadCount(0);
  }, [active]);

  useEffect(() => {
    if (chat.status !== "ready") return;
    const newBrandMessages = chat.messages.filter((message) => {
      const key = message.id ?? message.message_id ?? message.client_message_id;
      if (!key || seenMessagesRef.current.has(key)) return false;
      seenMessagesRef.current.add(key);
      return message.actor !== "customer";
    });

    if (!unreadInitializedRef.current) {
      unreadInitializedRef.current = true;
      return;
    }
    if (!active && newBrandMessages.length) {
      setUnreadCount((count) => count + newBrandMessages.length);
      if (settings.live_chat?.notifications_enabled === true) {
        playNotificationSound();
      }
    }
  }, [active, chat.messages, chat.status, settings.live_chat?.notifications_enabled]);

  useLayoutEffect(() => {
    const transcript = transcriptRef.current;
    if (!active || chat.status !== "ready" || !transcript) return;

    shouldStickToBottomRef.current = true;
    transcript.scrollTop = transcript.scrollHeight;
    previousScrollHeightRef.current = transcript.scrollHeight;
    lastTranscriptScrollTopRef.current = transcript.scrollTop;
  }, [active, chat.status]);

  useLayoutEffect(() => {
    const transcript = transcriptRef.current;
    if (!transcript) return;
    if (previousScrollHeightRef.current && !shouldStickToBottomRef.current) {
      transcript.scrollTop += transcript.scrollHeight - previousScrollHeightRef.current;
    } else if (shouldStickToBottomRef.current) {
      transcript.scrollTop = transcript.scrollHeight;
    }
    previousScrollHeightRef.current = transcript.scrollHeight;
    lastTranscriptScrollTopRef.current = transcript.scrollTop;
  }, [chat.messages]);

  const submitMessage = async (event) => {
    event.preventDefault();
    const message = draft.trim();
    if (
      !message ||
      chat.verification?.message_send_allowed !== true ||
      chat.awaitingAiReply ||
      chat.messageRequestInFlight
    ) return;
    setDraft("");
    shouldStickToBottomRef.current = true;
    await chat.sendMessage(message);
  };

  const messageSendAllowed =
    chat.verification?.message_send_allowed === true &&
    !chat.awaitingAiReply &&
    !chat.messageRequestInFlight;
  const remainingMessages = chat.verification?.remaining_unverified_messages;

  const requestOlderMessages = (transcript) => {
    const now = Date.now();
    if (
      !chat.nextBeforeId ||
      chat.loadingOlder ||
      (chat.olderRetryAt && chat.olderRetryAt > now) ||
      now - historyThrottleRef.current < LIVE_CHAT_LIMITS.historyScrollThrottleMs
    ) {
      return;
    }

    historyThrottleRef.current = now;
    previousScrollHeightRef.current = transcript.scrollHeight;
    shouldStickToBottomRef.current = false;
    chat.loadOlder();
  };

  useEffect(() => {
    if (
      chat.status === "ready" &&
      typeof chat.verification?.message_send_allowed !== "boolean"
    ) {
      console.error(
        "Live chat contract error: verification.message_send_allowed must be a Boolean."
      );
    }
  }, [chat.status, chat.verification?.message_send_allowed]);

  useEffect(() => {
    if (
      chat.status === "ready" &&
      chat.verification?.identity_verified === true &&
      chat.verification?.message_send_allowed === false
    ) {
      console.warn(
        "Live chat diagnostic: a verified customer is currently blocked from sending."
      );
    }
  }, [chat.status, chat.verification]);

  return (
    <section
      className="live-chat"
      style={{ "--live-chat-primary": settings["--site-widget-primary-color"] || "#151f32" }}
      aria-labelledby="live-chat-heading"
    >
      <header className="live-chat-header">
        <button type="button" onClick={onBack} aria-label="Back to widget home">
          <MdOutlineArrowBack />
        </button>
        <h1 id="live-chat-heading">{LIVE_CHAT_COPY.title}</h1>
      </header>
      {chat.status === "loading" || chat.status === "idle" || chat.postVerificationLoading ? (
        <ChatLoadingSkeleton />
      ) : chat.status === "error" ? (
        <div className="live-chat-centered" role="alert">
          <p>{chat.error || LIVE_CHAT_COPY.unavailable}</p>
          <button type="button" className="live-chat-primary-button" onClick={chat.retryBootstrap}>
            <MdRefresh aria-hidden="true" /> {LIVE_CHAT_COPY.retry}
          </button>
        </div>
      ) : (
        <>
          <div
            className="live-chat-transcript"
            ref={transcriptRef}
            aria-label="Chat messages"
            onScroll={(event) => {
              const element = event.currentTarget;
              const scrollingUp =
                element.scrollTop < lastTranscriptScrollTopRef.current;
              lastTranscriptScrollTopRef.current = element.scrollTop;
              shouldStickToBottomRef.current =
                element.scrollHeight - element.scrollTop - element.clientHeight < 80;
              if (scrollingUp && element.scrollTop <= 80) {
                requestOlderMessages(element);
              }
            }}
          >
            {chat.loadingOlder && (
              <div
                className="live-chat-history-loader"
                role="status"
                aria-label="Loading older messages"
              >
                <MdRefresh aria-hidden="true" />
              </div>
            )}
            {chat.olderError && (
              <p className="live-chat-history-error" role="alert">
                {chat.olderError}
              </p>
            )}
            {chat.messages.length === 0 && <p className="live-chat-empty">{LIVE_CHAT_COPY.empty}</p>}
            <ol aria-live="polite" aria-relevant="additions">
              {chat.messages.map((message) => (
                <Message
                  key={message.id || message.message_id || message.client_message_id}
                  message={message}
                  onRetry={chat.retryMessage}
                  onCancel={chat.cancelMessage}
                />
              ))}
            </ol>
          </div>

          {Number.isFinite(remainingMessages) && (
            <p className="live-chat-limit" role="status">
              {remainingMessages} message{remainingMessages === 1 ? "" : "s"} remaining before verification
            </p>
          )}
          {chat.error && <p className="live-chat-inline-error" role="alert">{chat.error}</p>}
          <IdentityForm chat={chat} />

          {chat.status !== "unsupported" && (
            <form className="live-chat-composer" onSubmit={submitMessage}>
              <label className="sr-only" htmlFor="live-chat-message">{LIVE_CHAT_COPY.inputLabel}</label>
              <textarea
                id="live-chat-message"
                rows="1"
                maxLength={LIVE_CHAT_LIMITS.maxMessageLength}
                value={draft}
                placeholder={LIVE_CHAT_COPY.inputPlaceholder}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey) submitMessage(event);
                }}
              />
              <button
                type="submit"
                disabled={!draft.trim() || !messageSendAllowed}
                aria-label={LIVE_CHAT_COPY.send}
              >
                <MdSend />
              </button>
            </form>
          )}
        </>
      )}
    </section>
  );
}
